/**
 * "Are this deck's card bodies actually on this client?" — the one predicate
 * every cold-deck rehydrate path must agree on.
 *
 * `loadedDeckIds` alone is NOT that predicate. It is flipped optimistically on
 * deck open (`markDeckUnarchived` right next to `requestDeckCards`), and it has
 * historically been recomputed from the server's "warm" activity list rather
 * than from resident bodies (fixed for the sync-diff path in PR #400 — "cold
 * decks no longer render permanently empty"). Any path that leaves a deck in
 * `loadedDeckIds` with an EMPTY card bucket while its meta says the deck HAS
 * cards used to render that deck permanently empty: the rehydrate fast-paths
 * saw "loaded", never re-requested, and `isHydrating` reported false — the
 * "tile badge says 11 cards, deck screen says create your first flashcards"
 * bug, reported on a `[BACKUP]` deck recovered from an R2 snapshot.
 *
 * The tie-breaker is `deckProperties.cardCount` — the persisted count the
 * archived-deck tiles already trust precisely BECAUSE it survives eviction:
 *
 *   - bucket holds ≥ `cardCount` rows  → resident (the bodies are here; the
 *                                        bucket may legitimately exceed the
 *                                        count, which excludes tombstones).
 *   - bucket holds fewer, `cardCount>0`→ NOT resident: either the loaded flag
 *                                        is lying with an empty bucket, or the
 *                                        bucket was PARTIALLY materialized —
 *                                        a live `cards-bulk-updated` frame
 *                                        creates a cold deck's bucket with
 *                                        just the pushed rows (no residency
 *                                        check on the apply path), and the
 *                                        next sync-diff then marked the deck
 *                                        "loaded" holding one card of N.
 *                                        Re-request the full set.
 *   - bucket empty, `cardCount` 0/unset→ genuinely empty deck: counting it
 *                                        resident keeps "loaded, empty" from
 *                                        spinning or refetching forever.
 *
 * A stale-high `cardCount` (the meta can lag a purge) costs one redundant
 * `request-deck-cards` per deck open — the reply is an idempotent merge —
 * which is the right side of the trade against rendering a partial deck.
 */

/** The slice of the sync store this predicate reads. Structural, so callers
 *  can pass `useFlashkaStore.getState()` and specs can pass a literal. */
export type DeckResidencyState = {
  cards: Record<string, Record<string, unknown> | undefined>;
  loadedDeckIds: ReadonlySet<string>;
  deckProperties: Record<string, { cardCount?: number | null } | undefined>;
};

export function deckCardsResident(state: DeckResidencyState, deckId: string): boolean {
  if (!state.loadedDeckIds.has(deckId)) return false;
  return (
    bucketRowCount(state.cards[deckId]) >= (state.deckProperties[deckId]?.cardCount ?? 0)
  );
}

/**
 * Did a server stream that just COMPLETED for this deck deliver nothing at
 * all, although the deck's meta says it has cards?
 *
 * `deck-cards-loaded` is a completion signal, not a success signal: UserDo
 * sends it after streaming whatever `loadCards` found — including NOTHING,
 * when the DO holds no row for the deck (a deck whose bodies never reached
 * this server; observed on a dev stack re-seeded from a client whose cold
 * decks were bodiless). A caller that reads the cards right after the wait
 * (`sharing.share`, copy-to-library, merge) then works on an EMPTY set and
 * ships a hollow deck that nobody can study, while the owner's own deck reads
 * "0 cards" next to a tile that says 189.
 *
 * Deliberately narrower than `!deckCardsResident`: a bucket that holds SOME
 * rows but fewer than `cardCount` is the stale-high count the residency
 * predicate tolerates (one redundant re-request), and the server's stream is
 * always the full set — so "fewer" is never a partial delivery. Zero rows
 * against a positive count has exactly one reading: the server has none.
 * Tombstones count as rows here on purpose (they arrive in the stream too), so
 * a deck emptied elsewhere while this device's meta still says N is NOT
 * flagged — the stream is non-empty.
 *
 * `cardCount` is UNKNOWN (not zero) on a deck that was created, forked, copied,
 * imported or restored and never card-edited — only `reconcileDeckCardCount`
 * writes it. Reading that as "expects nothing" would wave such a deck through
 * hollow, so the deck's study summary (`rowCount`: every row this device has
 * summarised, tombstones included — the same yardstick as the stream) stands in
 * when the meta has no count. With neither, the deck is taken as genuinely
 * empty: there is nothing left to compare against.
 */
export type DeckStreamState = DeckResidencyState & {
  deckSummaries?: Record<string, { rowCount?: number } | undefined>;
};

export function deckStreamDeliveredNothing(
  state: DeckStreamState,
  deckId: string,
): boolean {
  if (bucketRowCount(state.cards[deckId]) > 0) return false;
  const expected =
    state.deckProperties[deckId]?.cardCount ??
    state.deckSummaries?.[deckId]?.rowCount ??
    0;
  return expected > 0;
}

/**
 * Can a server-authoritative card set (a study-group mirror re-seed) be MERGED
 * into this deck's local bucket without destroying the personal half?
 *
 * The merge overlays the member's personal-scope fields (`studyState`,
 * `starred`, `notes`, …) from the LOCAL bucket onto the incoming shared cards.
 * When rows this device holds are NOT in the bucket the overlay misses them,
 * the merged cards come out with EMPTY personal arrays (`cleanCardForStore`
 * backfills `[]`), and the write replaces both the in-memory bucket and the
 * on-disk rows — silently erasing the member's study history for that deck on
 * this device, then pushing the emptied rows with a newer timestamp.
 *
 * So the merge is safe exactly when the bucket is the deck's FULL set
 * (`deckBucketComplete`), and never for a cold deck (bodies withheld).
 *
 * A disk read alone (`diskLoadedDeckIds`) is NOT enough. A COMPLETE read
 * already marks the deck loaded, so that flag adds only the short case: a
 * deck cold at this device's first sync, or — on web, where card rows are
 * keyed by id — a same-id copy that took the rows over. Merging into that
 * bucket filled the missing rows with empty history and wrote them, the
 * study-group history loss. The caller re-runs once the deck is resident.
 */
export type DeckMergeSafetyState = DeckBucketState;

export function deckCardsMergeSafe(state: DeckMergeSafetyState, deckId: string): boolean {
  if (state.archivedDeckIds.has(deckId)) return false;
  return deckBucketComplete(state, deckId);
}

/**
 * Does this deck's in-memory bucket hold the deck's FULL row set — so that a
 * per-deck summary computed from it is exact? The predicate the summary bridge
 * (`deck-summary/store-bridge.ts`) recomputes on.
 *
 *   - archived (cold): never. Bodies are withheld; a live frame can still
 *     create a partial bucket, and summarising that would publish wrong counts.
 *   - tracked in `loadedDeckIds`: `deckCardsResident` (the flag can lie).
 *   - otherwise — a deck created on this device after hydrate, or a warm deck
 *     whose frames are still streaming before any sync-diff stamped it loaded:
 *     complete once the bucket holds at least `cardCount` rows.
 *
 * A stale-high `cardCount` keeps a fully resident deck "incomplete" and its
 * summary marked stale until the meta catches up — the same conservative side
 * `deckCardsResident` takes (one redundant re-request there, a lagging count
 * here), and the bridge re-checks stale decks on every later tick.
 */
export type DeckBucketState = DeckResidencyState & {
  archivedDeckIds: ReadonlySet<string>;
};

/**
 * Rows in a card bucket, cached per bucket IDENTITY.
 *
 * The residency predicates run inside store selectors on every tick, and
 * `Object.keys` of a 5k-card bucket allocated a 5k-entry array each time. A
 * bucket is never mutated once the store publishes it — every write builds a
 * new bucket object (the dirty scan and the summary bridge both rely on that
 * identity) — so its size is a pure function of the object. Any object a
 * caller builds and then keeps adding keys to must not be passed here.
 */
const rowCountByBucket = new WeakMap<object, number>();

export function bucketRowCount(bucket: Record<string, unknown> | undefined): number {
  if (!bucket) return 0;
  let n = rowCountByBucket.get(bucket);
  if (n === undefined) {
    n = Object.keys(bucket).length;
    rowCountByBucket.set(bucket, n);
  }
  return n;
}

export function deckBucketComplete(state: DeckBucketState, deckId: string): boolean {
  if (state.archivedDeckIds.has(deckId)) return false;
  if (state.loadedDeckIds.has(deckId)) return deckCardsResident(state, deckId);
  const bucket = state.cards[deckId];
  if (!bucket) return false;
  const meta = state.deckProperties[deckId];
  // NO meta row at all: the deck is being created on this device right now, so
  // whatever the bucket holds IS the deck.
  if (meta === undefined) return true;
  // Meta exists but carries no count. `cardCount` is UNKNOWN, not zero — it is
  // only ever written by `reconcileDeckCardCount`, so a deck that was created,
  // forked, copied, imported or restored and never card-edited has none. The
  // old `?? 0` read that as "zero expected", so ANY non-empty bucket satisfied
  // it: one live `cards-bulk-updated` frame on a cold 50-card deck made its
  // single row "the full set", the bridge persisted a 1-card summary as
  // trusted, and the tile said 1 card. Unknown means we cannot claim complete.
  if (meta.cardCount == null) return false;
  return bucketRowCount(bucket) >= meta.cardCount;
}
